import { createImpedancePoint } from '@/lib/impedance';
import type { TestChartData, ChannelData } from '@/lib/impedance';
import type { MeasurementDto } from '@/features/harness/types';

/** Shape of one `ImpedanceData` entry as persisted by `impedanceResultToJson` (converters.ts). */
interface HarnessImpedanceChannelJson {
  channel?: string;
  magnitudeOhm?: number[];
  phaseRad?: number[];
}

interface HarnessImpedanceResultJson {
  frequencyHz?: number[];
  // Element may be null: an explicit JSON `null` array entry survives into this opaque payload.
  data?: (HarnessImpedanceChannelJson | null)[];
}

/** Shape of `MeasurementDto.resultJson` as persisted by useHarnessMeasurement.buildResultJson. */
interface HarnessResultJson {
  impedanceResult?: HarnessImpedanceResultJson | null;
}

/**
 * Maps a single harness Measurement into the `TestChartData` shape the shared
 * NyquistPlot/BodePlot components (`components/MeasurementAnalysis/Charts/`) consume — a
 * single curve in the `baseline` slot, `specimen` left null (there is no delta/comparison
 * concept in the harness, Story 1.4 Dev Notes).
 *
 * `resultJson.impedanceResult.data[]` (as persisted by `impedanceResultToJson`) already holds
 * one entry per channel, each carrying its own `channel` id and `magnitudeOhm`/`phaseRad`
 * arrays sharing `frequencyHz` — so this maps each entry directly to a `ChannelData`, no
 * de-interleaving or excitation-profile channel-count fallback needed.
 */
export function measurementToChartData(measurement: MeasurementDto): TestChartData {
  const result = measurement.resultJson as HarnessResultJson | null;
  const impedanceResult = result?.impedanceResult;

  const frequencies = impedanceResult?.frequencyHz ?? [];
  const data = impedanceResult?.data ?? [];

  let baseline: TestChartData['baseline'] = null;
  if (frequencies.length > 0 && data.length > 0) {
    const channels: ChannelData[] = data.map((channelJson, channelIndex) => {
      const magnitudeOhm = channelJson?.magnitudeOhm ?? [];
      const phaseRad = channelJson?.phaseRad ?? [];
      return {
        // Null-safe: a null array element (device omitted this channel) coerces to a
        // synthesized "CHn" label rather than throwing.
        channelId: channelJson?.channel ?? `CH${channelIndex + 1}`,
        channelIndex,
        points: frequencies.map((freq, i) =>
          createImpedancePoint(freq, magnitudeOhm[i] ?? 0, phaseRad[i] ?? 0)
        ),
      };
    });
    baseline = { channels, frequencies, channelCount: channels.length };
  }

  return {
    testId: measurement.id,
    label: `#${measurement.sequenceNumber}`,
    baseline,
    specimen: null,
  };
}
